import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {RouteService} from '../../../../services/route/route.service';
import {TransactionsService} from '../../../../services/transactions/transactions.service';
import {TranslateService} from '@ngx-translate/core';
import {FileData, UtilService} from '../../../../services/util/util.service';
import {cloneDeep, sumBy} from 'lodash-es';
import {
  AuditLogPage,
  Cart,
  CartEvent,
  CartTotalSurcharge,
  EventRight,
  GiftTicketReminder,
  LegacyTicket,
  PaymentMethodEnum,
  PaymentTypeEnum,
  PriceInfoEnum,
  PrintTicketType,
  PurchaseTransaction,
  SendType,
  SurchargeType,
  TicketCompatibility
} from '../../../../generated/sales-terminal-client';
import {PeriodPipe} from '../../../../pipes/period/period.pipe';
import {ValidationService} from '../../../../services/validation/validation.service';
import {ConfigureEmailDialogComponent} from '../../../../components/configure-email-dialog/configure-email-dialog.component';
import {GiftTicketService} from '../../../../services/giftticket/giftTicket.service';
import {MatDialog} from '@angular/material/dialog';
import {RefundService} from '../../../../services/refund/refund.service';
import {concat, forkJoin, Observable, of, Subject, tap, toArray} from 'rxjs';
import {AuthService} from '../../../../services/auth/auth.service';
import {CartService} from '../../../../services/cart/cart.service';
import {SnackService} from '../../../../services/snack/snack.service';
import {PendingChangesComponent} from '../../../../guards/pendingChanges/pending-changes-component';
import {EmailModalComponent} from '../email-modal/email-modal.component';
import {InvoiceGenerateModalComponent} from '../invoice-modal/invoice-generate-modal.component';
import {InvoiceService} from '../../../../services/invoice/invoice.service';
import {ViewService} from '../../../../services/view/view.service';
import {filter, finalize, mergeMap, takeUntil} from 'rxjs/operators';
import {Privileges} from '../../../admin/privileges/privileges.constants';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {DataTable, DataTableRowItemType} from "../../../../components/data-table/data-table.interfaces";
import {TransactionRefundModalComponent} from "../refund-modal/transaction-refund-modal.component";
import {TextInputType} from '../../../../components/forms/text-input/text-input.component';
import {TotalsTable} from '../../../../components/totals-table/totals-table.component';
import {PricePipe} from '../../../../pipes/price/price.pipe';

@Component({
  selector: 'app-transaction-detail',
  templateUrl: './transaction-detail.component.html',
  styleUrls: ['./transaction-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TransactionDetailComponent implements OnInit, AfterViewInit, PendingChangesComponent, OnDestroy {
  private readonly ngUnsubscribe = new Subject<void>();

  @ViewChild('tableTicketId')
  tableTicketId: TemplateRef<any>;
  @ViewChild('tableEvent')
  tableEvent: TemplateRef<any>;
  @ViewChild('tableDate')
  tableDate: TemplateRef<any>;
  @ViewChild('tableTicketType')
  tableTicketType: TemplateRef<any>;
  @ViewChild('tableTicketPrice')
  tableTicketPrice: TemplateRef<any>;
  @ViewChild('tableReminder')
  tableReminder: TemplateRef<any>;

  @HostListener('window:beforeunload')
  public canDeactivate(): Observable<boolean> | boolean {
    return !this.hasTransactionChanged;
  }

  public isLoading = false;
  transaction: PurchaseTransaction;
  selectedTicketIds: number[] = [];
  modalIsOpened = false;
  compatibleTickets: Array<number> = []; // Tickets that are part of the transaction
  refundableTickets: Array<number> = []; // Tickets that can be refunded
  notEventOwnerTickets: Array<number> = []; // Tickets that don't belong to event owner
  ticketReminderEmailMap: Map<number, string> = new Map<number, string>();
  hasRemindersEnabled: boolean;
  paymentTypes: PaymentTypeEnum[] = [];
  invoiceGenerateInProgress = false;
  selectedMobileTab: 'details' | 'tickets' | 'auditLogs' = 'details';
  public totalsTableData: TotalsTable;
  public paymentsTableData: TotalsTable;
  public auditLogPage: AuditLogPage;
  public auditPageNumber: number = 1;
  private auditPageSize: number = 30;

  public get isLegacy(): boolean {
    return this.routeService.checkRoute(['sales-terminal', 'transactions', 'legacy']);
  }

  private _clickableTickets: number[] = [];
  public get clickableTickets(): number[] {
    return this._clickableTickets || [];
  }

  public set clickableTickets(tickets: number[]) {
    this._clickableTickets = tickets;
    if (this._ticketId) {
      this.selectedTicketIds = [this._ticketId];
    } else {
      this.selectedTicketIds = cloneDeep(this._clickableTickets);
    }
  }

  paymentType: PaymentTypeEnum;
  paymentMethod: PaymentMethodEnum;




  paymentChanged = false;
  internalCommentChanged = false;
  clientNotificationEmailChanged = false;
  public Privileges = Privileges;

  public hasRefundRights: boolean;
  public isRefunded: boolean;
  public tableConfig: DataTable;
  private _ticketId: number;

  constructor(
    private dialog: MatDialog,
    private routeService: RouteService,
    private snackService: SnackService,
    private transactionsService: TransactionsService,
    public cartService: CartService,
    private giftTicketService: GiftTicketService,
    public authService: AuthService,
    private translateService: TranslateService,
    public utilService: UtilService,
    private refundService: RefundService,
    public periodPipe: PeriodPipe,
    public invoiceService: InvoiceService,
    public viewService: ViewService,
    private changeDetector: ChangeDetectorRef,
    private pricePipe: PricePipe) { }

  public ngOnInit(): void {
    if (!this.isLegacy && parseFloat(this.routeService.params['id']) < 100000000) {
      this.routeService.replaceUrl('/app/sales-terminal/transactions/legacy/view/' + this.routeService.params['id']);
      return;
    }
    this.hasRefundRights = this.authService.checkPrivileges([Privileges.REFUND_SALES_TERMINAL, Privileges.REFUND_WEB])
      || (this.isLegacy && this.authService.checkPrivileges([Privileges.REFUND_ALL, Privileges.REFUND_PROMOTER, Privileges.REFUND_EXPIRED]));
    this.getTransaction();
    this.routeService.queryParams.subscribe((params) => {
      if (params?.ticketid) {
        this._ticketId = Number(params.ticketid);
      }
    });
  }

  public ngAfterViewInit(): void {
    this.setupTable();
  }

  private getTransaction(): void {
    this.startLoader();
    this.transactionsService[this.isLegacy ? 'getTransactionLegacy' : 'getTransaction'](parseFloat(this.routeService.params['id'])).pipe(
      tap({ error: () => this.stopLoader() })
    ).subscribe((transaction) => {
      this.transaction = transaction;
      this.paymentType = this.getPaymentType()
      this.paymentMethod = this.getPaymentMethod()
      this.isRefunded = this.transaction.cart.total.total < 0;
      if (this.isLegacy) {
        this.checkCompatibility();
      } else {
        this.setRefundableTickets();
        this.stopLoader();
      }
      this.setupTicketReminderEmailMap(this.transaction.cart);
      this.fillDataTable();
      if (!this.authService.user.isNewTypeSalespoint && this.isLegacy) {
        this.cartService.getPaymentTypes().subscribe((paymentTypes) => {
          this.paymentTypes = paymentTypes;
          this.detectChanges();
        });
      } else {
        this.paymentTypes = [PaymentTypeEnum.Transfer, PaymentTypeEnum.NoPayment];
        this.detectChanges();
      }
      this.getAuditLogsAndTriggerUpdate();
    });
  }

  public setupTable(): void {
    this.tableConfig = {
      header: {
        items: [
          {
            key: 'selected',
            value: false,
            format: 'general.api_cart_table_ticket_id',
            type: DataTableRowItemType.Checkbox,
            cssClass: 'min-width',
            isHidden: this.clickableTickets?.length === 0 || this.isRefunded,
            disabled: !this.clickableTickets.length
          },
          {
            key: 'ticket_id',
            format: 'general.api_cart_table_ticket_id'
          },
          {
            key: 'event',
            format: 'general.api_cart_table_event_title',
            children: [
              {
                key: 'venue',
                format: 'general.api_cart_table_event_venue',
                cssClass: 'subtitle'
              }
            ]
          },
          {
            key: 'date',
            format: 'general.api_cart_table_date'
          },
          {
            key: 'sector',
            format: 'general.api_cart_table_sector',
            isHidden: !this.isLegacy
          },
          {
            key: 'row',
            format: 'general.api_cart_table_row',
            isHidden: !this.isLegacy
          },
          {
            key: 'seat',
            format: 'general.api_cart_table_seat',
            isHidden: !this.isLegacy
          },
          {
            key: 'ticketType',
            format: 'general.api_cart_table_type'
          },
          {
            key: 'ticketPrice',
            format: 'general.api_cart_table_price',
            cssClass: 'align-right'
          },
          {
            key: 'reminder',
            cssClass: 'align-right min-width',
            isHidden: !this.hasRemindersEnabled || this.isRefunded
          }
        ]
      },
      body: {
        items: [
          {
            key: 'selected',
            value: 'id',
            type: DataTableRowItemType.Checkbox,
            isHidden: this.clickableTickets?.length === 0 || this.isRefunded
          },
          {
            key: 'ticket_id',
            type: DataTableRowItemType.Template,
            template: this.tableTicketId
          },
          {
            key: 'event',
            useParent: true,
            type: DataTableRowItemType.Template,
            template: this.tableEvent
          },
          {
            key: 'date',
            useParent: true,
            type: DataTableRowItemType.Template,
            template: this.tableDate,
            cssClass: 'white-space-pre-line'
          },
          {
            key: 'sector',
            format: 'sectorName',
            useModel: true,
            isHidden: !this.isLegacy
          },
          {
            key: 'row',
            format: 'row',
            useModel: true,
            isHidden: !this.isLegacy
          },
          {
            key: 'place',
            format: 'place',
            useModel: true,
            isHidden: !this.isLegacy
          },
          {
            key: 'ticketType',
            type: DataTableRowItemType.Template,
            template: this.tableTicketType
          },
          {
            key: 'ticketPrice',
            type: DataTableRowItemType.Template,
            template: this.tableTicketPrice,
            cssClass: 'align-right'
          },
          {
            key: 'reminder',
            type: DataTableRowItemType.Template,
            template: this.tableReminder,
            cssClass: 'align-right white-space-no-wrap',
            isHidden: !this.hasRemindersEnabled || this.isRefunded
          }
        ],
        rowClass: [
          {
            func: (id: number): boolean => {
              return (!this.hasRefundRights || this.isCompatibleTicket(id)) && !this.isRefunded;
            },
            match: false,
            prop: 'id',
            cssClass: 'text-muted'
          }
        ]
      },
      footer: {
        items: []
      },
      customSelector: this.isLegacy ? 'legacyTickets' : 'tickets'
    }
    this.detectChanges();
  }

  public checkCompatibility(): void {
    const chunkSize = 50;
    let ticketIds: number[] = [];
    this.transaction.cart.events.forEach(event => {
      ticketIds = ticketIds.concat((this.isLegacy ? event.legacyTickets : event.tickets).map(t => t.id));
    })
    const arrayList: number[][] = ticketIds.reduce((resultArray: number[][], item: number, index) => {
      const chunkIndex = Math.floor(index / chunkSize);
      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = [];
      }
      resultArray[chunkIndex].push(item);
      return resultArray;
    }, []);
    if (arrayList?.length) {
      const requestArray: Observable<TicketCompatibility[]>[] = [];
      arrayList.forEach((ticketIdArray) => {
        requestArray.push(this.transactionsService.transactionCompatibilityCheck(this.transaction.id, ticketIdArray));
      });
      if (requestArray.length) {
        forkJoin(requestArray).pipe(
          finalize(() => this.stopLoader())
        ).subscribe((results: TicketCompatibility[][]) => {
          let joinedCompatibility: TicketCompatibility[] = [];
          requestArray.forEach((request, index) => {
            if (results[index]?.length) {
              joinedCompatibility = joinedCompatibility.concat(results[index]);
            }
          });
          this.compatibleTickets = joinedCompatibility.filter(t => !t.recycled).map(t => t.id);
          this.clickableTickets = [];
          this.transaction.cart.events.forEach(event => {
            this.clickableTickets = this.clickableTickets.concat((this.isLegacy ? event.legacyTickets : event.tickets).map(t => t.id).filter(ticket => this.compatibleTickets.includes(ticket)));
            this.setupTable();
          });
          this.setRefundableTickets();
        });
      } else {
        this.stopLoader();
      }
    } else {
      this.stopLoader();
    }
  }

  private setRefundableTickets(): void {
    this.refundableTickets = [];
    if (this.isLegacy) {
      const requestArray: Observable<EventRight>[] = [];
      this.transaction.cart.events.forEach(event => requestArray.push(this.refundService.canRefund(event.id)));
      if (requestArray.length) {
        forkJoin(requestArray).subscribe((results: EventRight[]) => {
          this.transaction.cart.events.forEach((event, index) => {
            if (results[index]?.refundAllowed) {
              this.refundableTickets = this.refundableTickets.concat((this.isLegacy ? event.legacyTickets : event.tickets).map(t => t.id));
            }
          });
          this.refundableTickets = this.refundableTickets.filter(t => this.compatibleTickets.includes(t));
          this.clickableTickets = cloneDeep(this.compatibleTickets);
          this.detectChanges();
        });
      }
    } else {
      this.clickableTickets = [];
      this.transaction.cart.events.forEach(event => {
        if (this.transaction.isTransactionOwner || event.ownerCentreId === this.authService.user.centreId) {
          event.tickets.forEach(ticket => {
            if (!ticket.refunded) {
              this.compatibleTickets.push(ticket.id);
              if (!this.transaction.isPaymentSentForProcessing && this.transaction.ticketsRefundAllowed?.includes(ticket.id)) {
                this.refundableTickets.push(ticket.id);
              }
              this.clickableTickets = [...this.clickableTickets, ticket.id];
            }
          })
        } else {
          event.tickets.forEach(ticket => {
            this.notEventOwnerTickets.push(ticket.id);
          })
        }
      });
      this.compatibleTickets = cloneDeep(this.clickableTickets);
    }
    this.setupTable();
  }

  public isCompatibleTicket(ticketId: number): boolean {
    return this.compatibleTickets.includes(ticketId);
  }

  public canRefundTicket(ticketId: number): boolean {
    return this.refundableTickets.includes(ticketId);
  }

  public isNotEventOwnerTicket(ticketId: number): boolean {
    return this.notEventOwnerTickets.includes(ticketId);
  }

  public get cartTotal(): number {
    return this.transaction.cart.total && (this.transaction.cart.total.ticketTotal + sumBy(this.cartHiddenFees, 'sum')) || 0;
  }

  public get cartHiddenFees(): CartTotalSurcharge[] {
    return (this.transaction.cart.total && this.transaction.cart.total.surcharges || [])
      .filter(fee => (fee.type === SurchargeType.Hidden));
  }

  public get cartSalespointFee(): CartTotalSurcharge[] {
    return (this.transaction.cart.total && this.transaction.cart.total.surcharges || [])
      .filter(fee => fee.name.some(t => t.value === 'general.api_fees_salespoint'));
  }

  public get cartTotalSum(): number {
    return this.transaction.cart.total && this.transaction.cart.total.total || 0;
  }

  get canDoPaymentReversal(): boolean {
    return this.authService.checkPrivileges(Privileges.SELF_SERVICE_TRANSACTIONS_PAYMENT_REVERSAL) && this.transaction?.paymentReversalAllowed && this.transaction?.salespoint?.superCentreId === this.authService.user.superCentreId && this.transaction.payments.filter(payment => {
      return payment.type === PaymentTypeEnum.GiftCard;
    }).length === 0;
  }

  public isTicketSelected(ticketId: number): boolean {
    return this.selectedTicketIds.includes(ticketId);
  }

  public isTicketClickable(id: number): boolean {
    return this.clickableTickets.some(item => item === id);
  }

  public areAllSelected(): boolean {
    return this.selectedTicketIds.length === this.clickableTickets.length;
  }

  public areSomeSelected(): boolean {
    return !!(this.selectedTicketIds.length && !this.areAllSelected());
  }

  public toggleTicket(checkboxChange: MatCheckboxChange, ticketId: number): void {
    if (checkboxChange.checked) {
      this.selectedTicketIds = [...this.selectedTicketIds, ticketId];
    } else {
      this.selectedTicketIds = this.selectedTicketIds.filter(id => id !== ticketId);
    }
  }

  public get areSelectedTicketsRefundable(): boolean {
    let result = true;
    this.selectedTicketIds.forEach(id => {
      if (!this.refundableTickets.includes(id)) result = false;
    });
    return result;
  }

  public toggleAll(checkboxChange: MatCheckboxChange): void {
    if (checkboxChange.checked) {
      this.selectedTicketIds = cloneDeep(this.clickableTickets);
    } else {
      this.selectedTicketIds = [];
    }
  }

  public startRefundTickets(): void {
    if (this.isLegacy) {
      this.refundService.addTickets({ tickets: this.selectedTicketIds }).subscribe(_ => {
        this.routeService.navigate('/app/sales-terminal/refund');
      });
    } else {
      const dialogRef = this.dialog.open(TransactionRefundModalComponent, {
        width: '50%',
        panelClass: 'xs-popup',
        data: {
          transaction: this.transaction,
          ticketIds: this.selectedTicketIds
        }
      });
      dialogRef.afterClosed().subscribe((data) => {
        if (data?.result) {
          this.startLoader();
          this.refundService.selfServiceRefund(this.transaction.id, {
            tickets: this.selectedTicketIds,
            paymentMethod: data.paymentMethod
          }).pipe(
            finalize(() => this.stopLoader()),
            takeUntil(this.ngUnsubscribe)
          ).subscribe(() => {
            if (data.paymentMethod === PaymentMethodEnum.EveryPay) {
              this.snackService.showSuccess(this.translateService.instant('general.api_transaction_payment_reversal_created'));
            } else {
              this.snackService.showSuccess(this.translateService.instant('general.api_transaction_refund_success'));
            }
            this.getTransaction();
          });
        }
      });
    }
  }

  public printTickets(): void {
    this.startLoader();
    const ticketPdfObservable = this.isLegacy
      ? this.cartService.printTransaction(this.transaction.id, PrintTicketType.Pdf, PriceInfoEnum.Regular, this.selectedTicketIds)
      : this.transactionsService.getTicketsPdfCombined(this.transaction.id, this.selectedTicketIds);
    ticketPdfObservable.pipe(
      finalize(() => this.stopLoader()),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((blob) => {
      const fileData: FileData = UtilService.getFileData('', PrintTicketType.Pdf);
      const file: Blob = new Blob([blob], { type: fileData.mime });
      const event: CartEvent = this.transaction.cart.events[0];
      const pdf_filename: string = event.title[0].value + '_' + this.periodPipe.transform(event.eventDate as any);
      if (this.utilService.isMobile) {
        this.utilService.mobileDownload(file, pdf_filename + '.pdf');
      } else {
        this.utilService.previewOrPrintFile(file, null, null, PrintTicketType.Pdf, pdf_filename);
      }
      this.getAuditLogsAndTriggerUpdate();
    })

  }

  public get canRefundTickets(): boolean {
    return this.selectedTicketIds.every(elem => this.refundableTickets.includes(elem));
  }

  public saveTransactionChanges(): void {
    const obsSequence = [];
    this.startLoader();
    if (this.clientNotificationEmailChanged) {
      if (this.isLegacy) {
        obsSequence.push(this.transactionsService.updateTransactionClientNotificationEmailLegacy(this.transaction.id, this.transaction.clientNotificationEmail));
      } else {
        obsSequence.push(this.transactionsService.updateTransactionClientNotificationEmail(this.transaction.id, this.transaction.clientNotificationEmail));
      }
    }
    if (this.internalCommentChanged) {
      if (this.isLegacy) {
        obsSequence.push(this.transactionsService.updateTransactionInternalCommentLegacy(this.transaction.id, this.transaction.internalComment));
      } else {
        obsSequence.push(this.transactionsService.updateTransactionInternalComment(this.transaction.id, this.transaction.internalComment));
      }
    }
    if (this.paymentChanged) {
      if (this.isLegacy) {
        obsSequence.push(this.transactionsService.updateTransactionPaymentType(this.transaction.id, { paymentType: this.paymentType }));
      } else {
        obsSequence.push(this.refundService.selfServiceUpdatePayment(this.transaction.id, this.paymentMethod));
      }
    }
    if (obsSequence.length) {
      concat(...obsSequence)
        .pipe(
          toArray(),
          finalize(() => this.stopLoader())
        )
        .subscribe({
          next: (responses: PurchaseTransaction[]) => {
            this.transaction = responses[responses.length - 1];

            if (!this.isLegacy && this.paymentChanged && this.transaction?.payments?.some(payment => payment.method?.startsWith(PaymentMethodEnum.EveryPay))) {
              this.snackService.showSuccess(this.translateService.instant('general.api_transaction_payment_reversal_created'));
            } else {
              this.snackService.showSuccess(this.translateService.instant('general.api_transaction_changes_saved'));
            }
            this.clientNotificationEmailChanged = false;
            this.internalCommentChanged = false;
            this.paymentChanged = false;
            this.getAuditLogsAndTriggerUpdate();
          }
        });
    }
  }

  private getPaymentType(): PaymentTypeEnum {
    if (this.transaction.payments.length == 0) return PaymentTypeEnum.NoPayment;
    let type = this.transaction.payments.find((p) => p.type !== PaymentTypeEnum.GiftCard).type;
    return type ? type : PaymentTypeEnum.NoPayment;

  }

  private getPaymentMethod(): PaymentMethodEnum {
    if (this.transaction.payments.length == 0) return undefined;
    let method = this.transaction.payments.find((p) => p.type !== PaymentTypeEnum.GiftCard).method;
    return method ? method as PaymentMethodEnum : undefined;
  }

  public isValidCustomerNotificationEmail(): boolean {
    return !this.transaction.clientNotificationEmail ? true : ValidationService.validateEmail(this.transaction.clientNotificationEmail);
  }

  public openGiftTicketReminderModal(ticket: LegacyTicket): void {
    const dialogRef = this.dialog.open(ConfigureEmailDialogComponent, {
      width: '40%',
      panelClass: 'xs-popup',
      data: {
        title: this.translateService.instant('general.api_gift_ticket_reminder_email_title'),
        description: this.translateService.instant('general.api_gift_ticket_reminder_email_description'),
        email: this.ticketReminderEmailMap.get(ticket.id),
        buttonOk: this.translateService.instant('general.api_configure_email_button_ok')
      }
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        const giftTicketReminder: GiftTicketReminder = { reminderEmail: res.email, ticketId: ticket.id };
        this.giftTicketService.updateGiftTickets(giftTicketReminder).subscribe(() => {
          this.ticketReminderEmailMap.set(ticket.id, res.email);
          this.snackService.showSuccess(this.translateService.instant('general.api_gift_ticket_reminder_update_success'));
        });
      }
    });
  }

  private setupTicketReminderEmailMap(cart: Cart): void {
    cart.events.forEach(event => {
      event.legacyTickets?.forEach(ticket => {
        if (!this.hasRemindersEnabled) {
          this.hasRemindersEnabled = this.hasGiftTicketReminderEnabled(event.reminderDays);
        }
        if (ticket.reminderEmail) {
          this.ticketReminderEmailMap.set(ticket.id, ticket.reminderEmail);
        }
      });
    })
  }

  public hasGiftTicketReminderEnabled(reminderDays: number): boolean {
    return reminderDays > 0;
  }

  // PENDING CHANGES
  public saveAndContinue(): void {
    this.saveTransactionChanges();
  }

  public get pendingChangesDescription(): string {
    return this.translateService.instant('general.api_description_unsaved_changes');
  }

  public get hasTransactionChanged(): boolean {
    return this.paymentChanged || this.internalCommentChanged || this.clientNotificationEmailChanged;
  }

  public get formattedCustomer(): string {
    const value = this.transaction.customer;
    let output = '';
    if (value?.firstName) {
      output += value.firstName;
    }
    if (value?.lastName) {
      output += ' ' + value.lastName;
    }
    /*if (value?.email) {
      if (!output.length) {
        output = value.email;
      } else {
        output += ' (' + value.email + ')'
      }
    }*/
    return output;
  }

  // INVOICE
  public openInvoiceEmailModal(invoiceUuid: string): void {
    const dialogRef = this.dialog.open(EmailModalComponent, {
      width: '50%',
      data: { invoiceUuid: invoiceUuid }
    });
    dialogRef.afterClosed()
      .subscribe(_ => this.getAuditLogsAndTriggerUpdate());
  }

  public openGenerateInvoiceModal(transaction: PurchaseTransaction): void {
    const dialogRef = this.dialog.open(InvoiceGenerateModalComponent, {
      maxHeight: '100%',
      width: '70%',
      data: {
        transaction: transaction,
        legacy: this.isLegacy
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.invoiceGenerateInProgress = true;
        this.invoiceService.generateInvoice(transaction.id, result, this.isLegacy).pipe(
          finalize(() => this.invoiceGenerateInProgress = false)
        ).subscribe((generatedInvoice) => {
          this.transaction.invoiceUuid = generatedInvoice.uuid;
          this.transaction.invoiceNumber = generatedInvoice.invoiceNumber;
          this.snackService.showSuccess(this.translateService.instant('general.api_invoice_generate_success'));
          this.invoiceService.downloadInvoice(generatedInvoice);
          this.getAuditLogsAndTriggerUpdate()
        });
      }
    });
  }

  public toList(): void {
    this.routeService.navigate('/app/sales-terminal/transactions' + (this.isLegacy ? '/legacy' : ''));
  }

  public changeTab(tab: number): void {
    if (tab === 0) {
      this.selectedMobileTab = 'details';
    } else if (tab === 1) {
      this.selectedMobileTab = 'tickets';
    } else if (tab === 2) {
      this.selectedMobileTab = 'auditLogs';
    }
  }

  public resendPurchaseEmail(): void {
    let confirmDialogRef = this.dialog.open(ConfigureEmailDialogComponent, {
      width: '60%',
      panelClass: 'xs-popup',
      data: {
        title: this.translateService.instant('general.api_transaction_resend_purchase_email_confirmation_title'),
        email: this.transaction.email,
        buttonOk: this.translateService.instant('general.api_transaction_resend_purchase_email_button_ok')
      }
    });
    confirmDialogRef.afterClosed().pipe(
      filter(res => res === true || res.email),
      mergeMap((res) => {
        if (this.isLegacy) {
          return this.cartService.sendTransaction(this.transaction.id, SendType.Client, PriceInfoEnum.Regular, this.selectedTicketIds, { email: res.email });
        } else {
          return this.transactionsService.resendPurchaseEmail(this.transaction.id, this.selectedTicketIds, res.email);
        }
      }),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(_ => {
      this.getAuditLogsAndTriggerUpdate();
      this.snackService.showSuccess(this.translateService.instant('general.api_transaction_tickets_sent_success'));
    })
  }

  private startLoader(): void {
    this.isLoading = true;
    this.detectChanges();
  }

  private stopLoader(): void {
    this.isLoading = false;
    this.detectChanges();
  }

  private detectChanges(): void {
    this.changeDetector.markForCheck();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public fillDataTable(): void {
    this.totalsTableData = {
      body: [
        { label: 'general.api_cart_table_items_total', value: this.transaction.cart.total.ticketQuantity, type: 'amount' },
        { label: 'general.api_cart_table_items_price_total', value: this.cartTotal, type: 'price' }
      ],
      footer: [
        {
          label: this.cartTotalSum >= 0 ? 'general.api_transaction_table_price_total' : 'general.api_transaction_table_price_total_return',
          value: this.cartTotalSum,
          type: 'price'
        }
      ]
    };
    this.cartSalespointFee.forEach(fee => {
      this.totalsTableData.body.push({ label: fee.name, value: fee.total, type: 'price' });
    });
    if (this.transaction.cart.total?.legacyDelivery) {
      this.totalsTableData.body.push({
        label: 'general.api_reports_delivery_fee',
        subValue: this.transaction.cart.total.legacyDelivery.name,
        value: this.transaction.cart.total.legacyDelivery.price,
        type: 'price'
      });
    }
    if (this.transaction.cart.total?.surcharges?.length) {
      this.transaction.cart.total.surcharges.filter(fee => !this.cartSalespointFee.includes(fee)).forEach(surcharge => {
        this.totalsTableData.body.push({
          label: surcharge.name,
          value: surcharge.total,
          type: 'price'
        });
      });
    }
    if (this.transaction.cart.total?.insurances?.length) {
      this.transaction.cart.total.insurances.forEach(insurance => {
        this.totalsTableData.body.push({
          label: 'general.api_transaction_table_insurance',
          subValue: this.pricePipe.transform(insurance.insurancePrice) + (insurance.insuranceCount > 1 ? ' x ' + insurance.insuranceCount : ''),
          value: insurance.insurancePrice * insurance.insuranceCount,
          type: 'price'
        });
      });
    }
    if (this.transaction.cart.total?.discountAmount) {
      this.totalsTableData.body.push({ label: 'general.api_cart_table_discount_total', value: this.transaction.cart.total.discountAmount * -1, type: 'price' });
    }
    if (this.transaction.cart?.total?.cashRounding) {
      this.totalsTableData.footer.push({ label: 'general.api_cart_table_cash_rounding', value: this.transaction.cart.total.cashRounding, type: 'price', isPrefix: true });
      this.totalsTableData.footer.push({
        label: this.cartTotalSum >= 0 ? 'general.api_transaction_table_price_total_in_cash' : 'general.api_transaction_table_price_total_return_in_cash',
        value: this.cartTotalSum + this.transaction.cart.total.cashRounding,
        type: 'price'
      });
    }

    if (this.transaction.payments?.length) {
      let paymentsTotal: number = 0;
      this.paymentsTableData = {
        header: [
          { label: 'general.api_cart_table_payment_methods', type: 'no-value' }
        ],
        body: [],
        footer: []
      }
      if (this.transaction.cart.giftCards?.length && this.transaction.cart.total?.giftCardCreditTotal) {
        this.paymentsTableData.body.push({
          label: 'general.api_cart_table_payment_' + PaymentTypeEnum.GiftCard,
          value: this.transaction.cart.total.giftCardCreditTotal,
          type: 'price'
        });
        paymentsTotal += this.transaction.cart.total.giftCardCreditTotal;
      }
      const giftCodes: string[] = [];
      this.transaction.payments.filter(payment => payment.type === PaymentTypeEnum.GiftCard).forEach(payment => {
        payment.giftCards?.forEach(card => {
          giftCodes.push(card.code + ' (' + this.pricePipe.transform(card.credit) + ')');
        });
      });
      if (giftCodes.length) {
        this.paymentsTableData.body.push({ label: giftCodes.join(', '), value: null, type: 'no-value' }); // add "title" when legal person name will be returned
      }
      this.transaction.payments.filter(payment => payment.type !== PaymentTypeEnum.GiftCard).forEach(payment => {
        if (payment.type === PaymentTypeEnum.Transfer) {
          this.paymentsTableData.body.push({ label: 'general.api_transactions_payment_method_' + payment.method, value: payment.total, type: 'price' });
        } else {
          this.paymentsTableData.body.push({ label: 'general.api_cart_table_payment_' + payment.type, subValue: payment.method, value: payment.total, type: 'price' });
        }
        paymentsTotal += payment.total;
      });
      this.paymentsTableData.footer.push({ label: 'general.api_cart_table_payment_methods_total', value: paymentsTotal, type: 'price' });
    }
  }

  public get canSeeAuditLogs(): boolean {
    return this.authService.hasAuditGetPrivilege;
  }

  public getAuditLogs(id: number): Observable<AuditLogPage> {
    if (!this.canSeeAuditLogs) {
      return of({});
    }
    if (this.isLegacy) {
      return this.transactionsService.getYboAuditLogs(id, this.auditPageNumber, this.auditPageSize);
    } else {
      return this.transactionsService.getAuditLogs(id, this.auditPageNumber, this.auditPageSize);
    }
  }

  public getAuditLogsAndTriggerUpdate(): void {
    this.getAuditLogs(this.transaction.id).subscribe((auditPage: AuditLogPage) => {
      this.auditLogPage = auditPage;
      this.detectChanges();
    });
  }

  protected readonly PaymentMethodValues = [PaymentMethodEnum.Transfer, PaymentMethodEnum.Card, PaymentMethodEnum.Cash];
  protected readonly PaymentMethodEnum = PaymentMethodEnum;
  protected readonly TextInputType = TextInputType;
}
